import { Manga } from '../types/manga';

export const FLAG_CODE_MAP: Record<string, string> = {
  '🇰🇷': 'kr',
  '🇨🇳': 'cn',
  '🇯🇵': 'jp',
  'เกาหลี': 'kr',
  'จีน': 'cn',
  'ญี่ปุ่น': 'jp',
};

export function getFlagUrl(nationality?: string): string | null {
  if (!nationality) return null;
  const code = FLAG_CODE_MAP[nationality.trim()];
  if (!code) return null;
  return `https://flagcdn.com/w40/${code}.png`;
}

export function normalizeNationality(val: string): string {
  if (!val) return '';
  const trimmed = val.trim();
  if (trimmed === 'เกาหลี' || trimmed === '🇰🇷') return '🇰🇷';
  if (trimmed === 'จีน' || trimmed === '🇨🇳') return '🇨🇳';
  if (trimmed === 'ญี่ปุ่น' || trimmed === '🇯🇵') return '🇯🇵';
  return trimmed;
}

export function getSourceName(url?: string): string {
  if (!url) return 'ไม่ระบุเว็บ';
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace(/^www\./i, '');
  } catch {
    const match = String(url).match(/^https?:\/\/([^\/?#]+)/i);
    return match ? match[1].replace(/^www\./i, '') : 'ไม่ระบุเว็บ';
  }
}

export function getTodayKey(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** "YYYY-MM-DD" or "D/M/YYYY" -> local midnight epoch ms */
function parseDayKey(raw?: string): number | null {
  if (!raw) return null;
  const s = raw.trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])).getTime();
  return null;
}

/**
 * When the newest chapter came out on the fastest site. Falls back to the day the tracker first saw
 * it (lastUpdatedAt) for manga not re-checked since release times were recorded.
 */
export function getLatestReleaseAt(manga: Manga): number | null {
  if (manga.latestChapterAt) return manga.latestChapterAt;
  const current = Number(manga.currentCh) || 0;
  const fromSources = (manga.sources || [])
    .filter((s) => s.chapterAt && (Number(s.chapter) || 0) === current)
    .map((s) => s.chapterAt as number);
  if (fromSources.length > 0) return Math.min(...fromSources);
  return parseDayKey(manga.lastUpdatedAt);
}

const isMidnight = (d: Date) => d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0;

/**
 * Thai relative time: "15 นาทีที่แล้ว", "3 ชม.ที่แล้ว", "เมื่อวาน", "3 วันที่แล้ว", "2 สัปดาห์ที่แล้ว".
 * Timestamps at exactly local midnight come from date-only sources, so they only say "วันนี้".
 */
export function formatRelativeThai(ts: number, now: number = Date.now()): string {
  const diffMs = Math.max(0, now - ts);
  const then = new Date(ts);
  const dayOnly = isMidnight(then);

  if (!dayOnly && diffMs < 24 * 60 * 60 * 1000) {
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'เมื่อสักครู่';
    if (mins < 60) return `${mins} นาทีที่แล้ว`;
    return `${Math.floor(mins / 60)} ชม.ที่แล้ว`;
  }

  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOfDay(new Date(now)) - startOfDay(then)) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'วันนี้';
  if (days === 1) return 'เมื่อวาน';
  if (days < 7) return `${days} วันที่แล้ว`;
  if (days < 30) return `${Math.floor(days / 7)} สัปดาห์ที่แล้ว`;
  if (days < 365) return `${Math.floor(days / 30)} เดือนที่แล้ว`;
  return `${Math.floor(days / 365)} ปีที่แล้ว`;
}

export function isUpdatedToday(manga: Manga): boolean {
  const hasUnread = Number(manga.readCh) < Number(manga.currentCh);

  // With a known release time, "today" means the chapter came out today, not that the tracker
  // happened to notice it today (a first sync can catch up on chapters released days ago)
  if (manga.latestChapterAt) {
    const released = new Date(manga.latestChapterAt);
    const now = new Date();
    const sameDay =
      released.getFullYear() === now.getFullYear() &&
      released.getMonth() === now.getMonth() &&
      released.getDate() === now.getDate();
    return sameDay && hasUnread;
  }

  if (!manga.lastUpdatedAt) return false;
  const today = getTodayKey();

  // Format check: "YYYY-MM-DD" or "D/M/YYYY" or "DD/MM/YYYY"
  const raw = manga.lastUpdatedAt.trim();
  let matches = raw === today;
  if (!matches && raw.includes('/')) {
    const parts = raw.split('/');
    if (parts.length === 3) {
      const d = parts[0].padStart(2, '0');
      const m = parts[1].padStart(2, '0');
      const y = parts[2];
      matches = `${y}-${m}-${d}` === today;
    }
  }

  return matches && hasUnread;
}

export function getStatusBadge(manga: Manga): {
  label: string;
  badgeClass: string;
  isCaughtUp: boolean;
  unreadCount: number;
} {
  const read = Number(manga.readCh) || 0;
  const current = Number(manga.currentCh) || 0;
  const isCaughtUp = read >= current;
  const unreadCount = Math.max(0, current - read);
  const status = (manga.status || 'ยังไม่จบ').trim();

  if (status === 'จบแล้ว' && isCaughtUp) {
    return {
      label: 'จบแล้ว',
      badgeClass: 'bg-sky-400/15 text-sky-300',
      isCaughtUp: true,
      unreadCount: 0,
    };
  }

  if (status.startsWith('จบภาค') && isCaughtUp) {
    return {
      label: status,
      badgeClass: 'bg-blue-400/15 text-blue-300',
      isCaughtUp: true,
      unreadCount: 0,
    };
  }

  if (isCaughtUp) {
    return {
      label: 'ทันล่าสุด',
      badgeClass: 'bg-emerald-400/15 text-emerald-300',
      isCaughtUp: true,
      unreadCount: 0,
    };
  }

  return {
    label: `ดอง ${unreadCount} ตอน`,
    badgeClass: 'bg-amber-400/15 text-amber-300',
    isCaughtUp: false,
    unreadCount,
  };
}

export const DEFAULT_COVER_PLACEHOLDER =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 400" width="300" height="400">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#18181b" />
          <stop offset="50%" stop-color="#111113" />
          <stop offset="100%" stop-color="#09090b" />
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#bg)" />
      <g fill="none" stroke="#52525b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" transform="translate(118, 160) scale(2.6)">
        <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z"/>
        <path d="M6 6h10"/>
        <path d="M6 10h10"/>
      </g>
      <text x="150" y="245" fill="#71717a" font-family="sans-serif" font-size="13" font-weight="600" text-anchor="middle">MANGA COVER</text>
    </svg>
  `.trim());

export function formatImageUrl(url?: string): string {
  if (!url || !url.trim()) {
    return DEFAULT_COVER_PLACEHOLDER;
  }
  let clean = url.trim();

  // Fix known oversized (>12MB) animated GIFs that crash mobile WebKit memory
  if (clean.includes('1BFMCMfrM8Rb_BE-2AqPhsLrNlITrHRNG')) {
    return 'https://www.up-manga.com/wp-content/uploads/2023/10/Reaper-of-the-Drifting-Moon.jpg';
  }
  if (clean.includes('19TcM2uIcWMaJnn7iGhgc28NMcoWkmX9x')) {
    return 'https://www.up-manga.com/wp-content/uploads/2023/09/Magic-Emperor-%E0%B8%A3%E0%B8%B2%E0%B8%8A%E0%B8%B2%E0%B8%88%E0%B8%AD%E0%B8%A1%E0%B9%80%E0%B8%A7%E0%B8%97%E0%B8%A2%E0%B9%8C.jpg';
  }

  // If it's a google drive file view link, convert to direct image
  const driveMatch = clean.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/i);
  if (driveMatch) {
    return `https://lh3.googleusercontent.com/d/${driveMatch[1]}`;
  }
  return clean;
}

export function formatBackupDate(isoString: string): string {
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
}

